// components/Footer.tsx
import React, { useEffect, useState } from 'react';
import { footerData } from '../data/footerData';
import { ArrowUp, Mail, MapPin, Phone, Sparkles, Hash, ArrowRight, Shield, TrendingUp, Zap } from 'lucide-react';
import MultiplexAd from "./Ads/MultiplexAd";
import AdManager from "./Ads/AdManager";

// Define proper footer item type
interface FooterItem {
  label: string;
  link: string;
  badge?: string; // Optional badge property
}

// Update your footerData structure in data/footerData.ts to include badge property

const Footer = () => {
  const [showScroll, setShowScroll] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Generate A-Z links
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('');

  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      // Replace with your actual endpoint
      const res = await fetch('YOUR_SUBSCRIBER_ENDPOINT', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setStatus('success');
        setEmail('');
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        throw new Error();
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <>
      <footer className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-950 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500 to-transparent animate-pulse"></div>
        </div>

        <div className="relative z-10">
          {/* AD SLOT 1: Pre-Footer Banner (Optional - Remove if too many ads) */}
          { 
            <div className="mt-8 pt-8 border-t border-gray-800">
              <AdManager 
                type="native"
                position="footer-recommendations"
                context="ai-tools"
                className="max-w-md mx-auto"
              />
            </div>
          }

          {/* Main Footer Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 lg:gap-10">
              {/* Brand Column */}
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-8 h-8 text-purple-400" />
                  <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    TooliQ.ai
                  </span>
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed max-w-md">
                  World's largest AI tools directory with 5000+ innovative solutions. 
                  Daily updates, expert reviews, and comprehensive comparisons.
                </p>
                
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-gray-800/30 rounded-lg p-3 text-center backdrop-blur-sm border border-gray-700/50">
                    <div className="text-2xl font-bold text-purple-300 mb-1">5K+</div>
                    <div className="text-xs text-gray-400">AI Tools</div>
                  </div>
                  <div className="bg-gray-800/30 rounded-lg p-3 text-center backdrop-blur-sm border border-gray-700/50">
                    <div className="text-2xl font-bold text-purple-300 mb-1">100+</div>
                    <div className="text-xs text-gray-400">Categories</div>
                  </div>
                  <div className="bg-gray-800/30 rounded-lg p-3 text-center backdrop-blur-sm border border-gray-700/50">
                    <div className="text-2xl font-bold text-purple-300 mb-1">10K+</div>
                    <div className="text-xs text-gray-400">Users</div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300 group">
                    <div className="p-2 bg-purple-600/20 rounded-lg group-hover:bg-purple-600/30 transition-colors">
                      <MapPin className="w-4 h-4 text-purple-400" />
                    </div>
                    <span className="text-sm">San Francisco, CA</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300 group">
                    <div className="p-2 bg-purple-600/20 rounded-lg group-hover:bg-purple-600/30 transition-colors">
                      <Mail className="w-4 h-4 text-purple-400" />
                    </div>
                    <a href="mailto:info@tooliq.ai" className="text-sm hover:text-white transition-colors">
                      info@tooliq.ai
                    </a>
                  </div>
                </div>
              </div>

              {/* Navigation Columns */}
              {footerData.map((col, idx) => (
                <div key={idx}>
                  <h4 className="text-white font-semibold mb-4 tracking-wide text-sm uppercase text-purple-300">
                    {col.title}
                  </h4>
                  <ul className="space-y-3">
                    {(col.items as FooterItem[]).map((item, i) => (
                      <li key={i}>
                        <a
                          href={item.link}
                          className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-200 flex items-center gap-2 group"
                        >
                          <span className="w-1 h-1 bg-purple-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                          {item.label}
                          {item.badge && (
                            <span className="text-xs bg-purple-600/30 text-purple-300 px-1.5 py-0.5 rounded">
                              {item.badge}
                            </span>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* A-Z Alphabet Navigation */}
              <div>
                <h4 className="text-white font-semibold mb-4 tracking-wide text-sm uppercase text-purple-300 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  AI Tools A-Z
                </h4>
                
                {/* Quick Stats */}
                <div className="mb-4 p-3 bg-gray-800/30 rounded-lg border border-gray-700/50">
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                    <span>New Tools This Week</span>
                    <span className="text-green-400">+42</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Total Categories</span>
                    <span className="text-purple-400">127</span>
                  </div>
                </div>

                {/* Compact Alphabet Grid */}
                <div className="grid grid-cols-6 gap-1.5 mb-3">
                  {alphabet.map(letter => (
                    <a
                      key={letter}
                      href={`/alphabet/${letter.toLowerCase()}`}
                      className="text-gray-300 hover:text-white hover:bg-purple-600/20 transition-all duration-200 text-center p-1.5 rounded text-xs font-medium border border-gray-700 hover:border-purple-500/50 hover:scale-105 min-w-[30px] relative group"
                      title={`Browse AI tools starting with ${letter}`}
                    >
                      {letter}
                      <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {letter === 'A' ? '3' : letter === 'C' ? '5' : letter === 'G' ? '2' : ''}
                      </span>
                    </a>
                  ))}
                </div>

                {/* Full Directory Link */}
                <div className="mt-4 pt-3 border-t border-gray-700/50">
                  <a 
                    href="/alphabet"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-xs font-medium transition-colors group"
                  >
                    <span>Browse Full Directory</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>

                {/* AD SLOT 2: Sidebar Native Ad (Optional) */}
                {/* 
                <div className="mt-6">
                  <AdManager 
                    type="native"
                    position="footer-sidebar"
                    context="directory"
                    className="rounded-xl overflow-hidden"
                  />
                </div>
                */}
              </div>

              {/* Newsletter Column */}
              <div className="lg:col-span-2">
                <h4 className="text-white font-semibold mb-4 tracking-wide text-sm uppercase text-purple-300 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  AI Insights Newsletter
                </h4>
                
                {/* Benefit Points */}
                <div className="mb-4 space-y-2">
                  <div className="flex items-center gap-2 text-gray-300 text-sm">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>Weekly AI tool discoveries</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300 text-sm">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                    <span>Exclusive discounts & deals</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300 text-sm">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    <span>Industry trends & analysis</span>
                  </div>
                </div>

                <form onSubmit={handleSubscribe} className="space-y-3 mb-6">
                  <div className="relative">
                    <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="email"
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-800/50 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm transition-all"
                      aria-label="Email address for newsletter subscription"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-purple-500/25 flex items-center justify-center gap-2 group"
                  >
                    <span>Subscribe Free</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                  {status === 'success' && (
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg animate-pulse">
                      <p className="text-green-400 text-sm text-center">🎉 Welcome to the AI revolution!</p>
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-red-400 text-sm text-center">⚠️ Please try again or contact support</p>
                    </div>
                  )}
                </form>

                <p className="text-gray-400 text-xs text-center">
                  Join 10,000+ AI professionals. No spam, unsubscribe anytime.
                </p>
              </div>
            </div>

            {/* AD SLOT 3: Middle Multiplex Ad (Optional) */}
            {/* 
            <div className="my-8">
              <MultiplexAd key="footer-middle-multiplex" />
            </div>
            */}
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800/50 relative">
            {/* AD SLOT 4: Pre-Bottom Banner (Optional) */}
            {/* 
            <div className="max-w-7xl mx-auto px-4 pt-4">
              <AdManager 
                type="leaderboard"
                position="footer-bottom"
                context="directory"
                className="mb-4"
              />
            </div>
            */}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="text-gray-400 text-sm">
                  © {new Date().getFullYear()} <span className="text-purple-400 font-semibold">TooliQ.ai</span>. 
                  <span className="hidden md:inline"> The world's leading AI tools directory.</span>
                </div>
                
                {/* Social Proof */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Shield className="w-3 h-3" />
                    <span>GDPR Compliant</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <TrendingUp className="w-3 h-3" />
                    <span>10K+ Monthly Visitors</span>
                  </div>
                </div>

                <div className="flex items-center gap-6 text-sm text-gray-400">
                  <a href="/privacy-policy" className="hover:text-white transition-colors hover:underline">Privacy</a>
                  <a href="/terms" className="hover:text-white transition-colors hover:underline">Terms</a>
                  <a href="/cookies" className="hover:text-white transition-colors hover:underline">Cookies</a>
                  <a href="/dmca" className="hover:text-white transition-colors hover:underline">DMCA</a>
                  <a href="/contact" className="text-purple-400 hover:text-purple-300 transition-colors font-medium">Contact</a>
                </div>
              </div>

              {/* Copyright Details */}
              <div className="mt-4 text-center md:text-left">
                <p className="text-gray-500 text-xs">
                  All AI tool names, logos, and trademarks are property of their respective owners. 
                  TooliQ.ai is not affiliated with any AI tool providers.
                </p>
              </div>

              {/* Partnership Badges */}
              <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-4">
                <div className="text-xs text-gray-500 border border-gray-700/50 px-3 py-1.5 rounded-full">
                  🛡️ SSL Secured
                </div>
                <div className="text-xs text-gray-500 border border-gray-700/50 px-3 py-1.5 rounded-full">
                  🤖 AI-Powered Curation
                </div>
                <div className="text-xs text-gray-500 border border-gray-700/50 px-3 py-1.5 rounded-full">
                  🔍 Daily Updates
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Enhanced JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://tooliq.ai/#organization",
                "name": "TooliQ.ai",
                "url": "https://tooliq.ai",
                "description": "World's largest AI tools directory with comprehensive reviews and comparisons",
                "foundingDate": "2023",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "San Francisco",
                  "addressRegion": "CA",
                  "addressCountry": "US"
                },
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "email": "info@tooliq.ai"
                }
              }
            ]
          }),
        }}
      />

      {/* Scroll-to-top Button */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-3 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 group"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
          <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            ↑
          </span>
        </button>
      )}
    </>
  );
};

export default Footer;